import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { getMasterData } from 'services/master-data';
import { IConversation, IUser } from 'types/chat';
import ChatItem from '../components/chat-item';
import { useTranslation } from 'react-i18next';
import { useOnClickOutside } from 'hooks/use-on-click-outside.hook';
import UserItem from '../components/user-item';
import { useChatContext } from '../context';
import { ContactType, ReadType } from '../consts';
import { openModal } from 'services/modal';
import CreateGroupModal from '../components/create-group-modal';
import { getListContactBySearch } from 'api/chat/list-contact';
import { getUser } from 'api/chat/user/get-user';
import { useSubscription } from 'hooks/use-subscription.hook';

interface ILogo {
  logo: string;
  slogan: string;
}

interface ISidebar {
  contactType: ContactType;
}

export default function Sidebar({ contactType }: ISidebar) {
  const [logo, setLogo] = useState<ILogo>();
  const [readType, setReadType] = useState<ReadType>(ReadType.ALL);
  const [keySearch, setKeySearch] = useState<string>('');
  const [showSearchList, setShowSearchList] = useState<boolean>(false);
  const [conversations, setConversations] = useState<IConversation[]>([]);
  const [listUser, setListUser] = useState<IUser[]>([]);
  const [users, setUsers] = useState<IUser[]>([]);
  const searchRef = useRef(null);
  const { t } = useTranslation();
  const chatContext = useChatContext();
  const subscription = useSubscription();

  useOnClickOutside(searchRef, () => {
    setShowSearchList(false);
  });

  const handleSearch = useCallback(
    (keySearch) => {
      const searchSub = getListContactBySearch(keySearch).subscribe({
        next: (res) => {
          setListUser(res.data.filterUser);
          setConversations(res.data.filterGroups);
        },
      });

      subscription.add(searchSub);
    },
    [subscription]
  );

  const onSearchChange = _.debounce((e) => {
    setKeySearch(e.target.value);
    handleSearch(e.target.value);
  }, 200);

  useEffect(() => {
    getMasterData('getGeneral').subscribe((res) => {
      setLogo({
        logo: res.data.find((item) => item.key === 'linkAvatar')?.value || '',
        slogan: res.data.find((item) => item.key === 'slogan')?.value || '',
      });
    });
  }, []);

  const renderSearchContacts = () => {
    return conversations.length ? (
      conversations.map((item, idx) => <ChatItem item={item} key={idx} />)
    ) : (
      <div>{conversations.length || listUser.length ? '' : `Không tìm thấy kết quả cho từ khóa "${keySearch}"`}</div>
    );
  };

  const createGroupChat = useCallback(() => {
    const modal = openModal(CreateGroupModal, {
      className: 'modal-chat-create-group',
      data: { type: 'add', title: 'Tạo nhóm chat' },
    });

    modal.afterClosed().subscribe();
  }, []);

  const getContacts = useCallback(() => {
    chatContext.setContactType(ContactType.USERS);

    const getUserSub = getUser('').subscribe((res) => {
      setUsers(res.data);
    });

    subscription.add(getUserSub);
  }, [chatContext, subscription]);

  return (
    <aside className='main-sidebar sidebar-main-wrapper'>
      <Link to='/' className='brand-link'>
        <img
          src={logo?.logo}
          alt='Logo'
          className='brand-image img-circle elevation-3'
          style={{ opacity: '.8', maxWidth: '41px', maxHeight: '41px', objectFit: 'cover', filter: 'brightness(1.5)' }}
        />
        <div className='brand-text font-weight-light' style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {logo?.slogan}
        </div>
      </Link>
      <div className='sidebar-main'>
        <div className='h-d_flex block-tab-chat '>
          <div
            className={clsx('sidebar-chats', { 'active-sidebar': contactType === ContactType.CONVERSATION })}
            onClick={() => chatContext.setContactType(ContactType.CONVERSATION)}
          >
            Chats
          </div>
          <div
            className={clsx('sidebar-chats', { 'active-sidebar': contactType === ContactType.USERS })}
            onClick={getContacts}
          >
            Users List
          </div>
          <div className='sidebar-chats' onClick={createGroupChat}>
            Tạo group
          </div>
        </div>

        {contactType === ContactType.CONVERSATION && (
          <>
            <div className='sidebar-option'>
              <button className='sidebar-btn' onClick={() => setReadType(ReadType.ALL)}>
                {t('page.maximize-chat.label.all')}
              </button>
              <button className='sidebar-btn' onClick={() => setReadType(ReadType.UNREAD)}>
                {t('page.maximize-chat.label.unread')}
              </button>
            </div>

            <div className='sidebar-search'>
              <i className='fas fa-search'></i>
              <input
                name=''
                placeholder={t('page.maximize-chat.label.search')}
                onChange={onSearchChange}
                ref={searchRef}
                onClick={() => {
                  setShowSearchList(true);
                }}
              />
            </div>

            {!showSearchList && (
              <div className='sidebar-pinned-chat'>
                <div className='pinned-chat'>
                  {chatContext.pinnedConversations.map((item, idx) => (
                    <ChatItem item={item} key={idx} isPinChat={true} />
                  ))}
                </div>
              </div>
            )}

            {readType === ReadType.ALL && (
              <div className='sidebar-main-chat'>
                <div className='main-chat'>
                  <div>
                    {showSearchList && renderSearchContacts()}
                    {!showSearchList &&
                      chatContext.conversations.map((item, idx) => <ChatItem item={item} key={idx} />)}
                  </div>
                  <div>
                    <div>{listUser.length ? 'Đồng nghiệp' : ''}</div>
                    {listUser.map((item, idx) => (
                      <UserItem item={item} key={idx} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {readType === ReadType.UNREAD && (
              <div className='sidebar-main-chat'>
                <div className='main-chat'>
                  {chatContext.conversations.map((item, idx) => {
                    if (item.numOfUnreadMessage) return <ChatItem item={item} key={idx} />;

                    return null;
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {contactType === ContactType.USERS && (
          <div className='sidebar-users'>
            <div className='sidebar-main-chat'>
              <div className='main-chat'>
                {users.map((item, idx) => (
                  <UserItem item={item} key={idx} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
